import * as React from "react";

import { Box, Link, Typography } from "@material-ui/core";
import { useMutation } from "react-query";
import * as Redux from "react-redux";

import { MopButton } from "client/components";
import { OnboardingStatusContext, StatusContext } from "client/context";
import {
  OnboardingStatusToCurrentStateMap,
  Status,
  StatusLabel,
  StatusMap,
  StatusStep,
  useStyles,
} from "client/domain/OnboardingStatus";
import {
  useBranding,
  useMerchantModel,
  useRunOnce,
  useTranslation,
} from "client/hooks";
import { logAmplitudeEvent } from "client/lib/amplitude";
import { OnboardingStatusService } from "client/services";
import { errorActions } from "client/store";

export const determineCurrentState: (
  params: Pick<
    OnboardingStatusContext,
    "inStoreTrainingComplete" | "onboardingStatus"
  >
) => Status = ({ inStoreTrainingComplete, onboardingStatus }) => {
  if (inStoreTrainingComplete) {
    return "Done";
  }

  return onboardingStatusToCurrentStateMap[onboardingStatus];
};

const onboardingStatusToCurrentStateMap: OnboardingStatusToCurrentStateMap = {
  INELIGIBLE: "Declined",
  NO_RECORD: "None",
  IN_PROGRESS: "None",
  IN_REVIEW: "InProgress",
  REQUIRES_ATTENTION: "Paused",
  READY_TO_INSTALL: "InProgress",
  COMPLETE: "InProgress",
};

export const InStoreTrainingStep: React.FC = () => {
  const t = useTranslation();
  const { brandTitle, brandKey } = useBranding();
  const dispatch = Redux.useDispatch();
  const { setMerchant } = useMerchantModel();
  const classes = useStyles();
  const {
    inStoreTrainingComplete,
    inStoreTrainingButtonClicked,
    onboardingStatus,
    onTrainingButtonClicked,
    registeredCountry,
  } = React.useContext<OnboardingStatusContext>(StatusContext);

  const textBoxWidth = brandKey === "CashApp" ? "70%" : "75%";

  const { mutate: updateInStoreTrainingStep } = useMutation(
    OnboardingStatusService.updateInStoreFields,
    {
      onError: (err: Error) => {
        dispatch(errorActions.setError(err.message));
      },
      onSuccess: (restoreResponse) => {
        if (restoreResponse) {
          setMerchant(restoreResponse);
        }
      },
    }
  );

  const currentState = determineCurrentState({
    inStoreTrainingComplete,
    onboardingStatus,
  });

  const statusMap: StatusMap = {
    None: {},
    InProgress: {
      className: classes.light,
    },
    Done: {
      className: classes.dark,
    },
    Paused: {},
    Reviewing: {
      className: classes.light,
    },
    RequiresAttention: {},
    Declined: {
      className: classes.light,
    },
  };

  const className = inStoreTrainingComplete
    ? classes.dark
    : statusMap[currentState];

  useRunOnce(() =>
    logAmplitudeEvent("STATUS_PAGE", "instore_training_step_card_loaded")
  );

  const onClickBeforeTrainingButtonClicked = () => {
    logAmplitudeEvent("GO_TO_TRAINING", "go_to_training");
    window.open(t("uiInstoreTrainingLink"), "_blank");
    if (onTrainingButtonClicked) {
      onTrainingButtonClicked();
    }
  };

  const onClickAfterTrainingButtonClicked = () => {
    logAmplitudeEvent("TRAINING_DONE", "training_done");
    updateInStoreTrainingStep({ inStoreTrainingComplete: true });
  };

  return (
    <StatusStep
      timeToFade={500}
      activeOnboardingStep="InStoreTraining"
      // TODO: Right now we don't have a good content strategy for this step since there are too
      // many internal states. Since it's the first step after review it's ok not to change on this step
      // but we should have some longer term solution around the content for this
      // stepStatus={currentState}
    >
      <Box className={`${className} ${classes.statusWrapper}`} mb={2} p={2}>
        <Box alignItems="center" display="flex" justifyContent="space-between">
          <Typography variant="h6">
            <strong>{brandTitle} Store Training</strong>
          </Typography>
          <StatusLabel status={currentState} />
        </Box>
        {currentState === "InProgress" && (
          <Box
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            mt={1}
          >
            <Box width={textBoxWidth} pl={0}>
              <ul className={classes.stepBulletList}>
                <li>
                  <Typography>
                    Go to the {brandTitle} Store Training to learn more about
                    “What is {brandTitle}”, “Why {brandTitle}” and “How it
                    Works”
                  </Typography>
                </li>
              </ul>
              {registeredCountry === "Australia" && (
                <>
                  <Typography>
                    {t("uiStatusInStoreTrainingFollowUpText", {
                      brandTitle,
                    })}
                  </Typography>
                </>
              )}
              {registeredCountry === "United Kingdom" && (
                <>
                  <ul className={classes.stepBulletList}>
                    <li>
                      <Typography>
                        {t("uiAdditionalInstoreTrainingResource")}
                        <Link
                          href={t("uiTrainingPosterJobAidLink")}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => {
                            logAmplitudeEvent(
                              "STATUS_PAGE",
                              "instore_training_card_poster_link_clicked"
                            );
                          }}
                        >
                          here
                        </Link>
                      </Typography>
                    </li>
                  </ul>
                  <Box>
                    <Typography className={classes.ukAgreement}>
                      {t("uiStoreTrainingAcknowledgement")}
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            <Box
              alignItems="center"
              display="flex"
              flexDirection="column"
              justifyContent={
                inStoreTrainingButtonClicked ? "space-evenly" : "flex-start"
              }
            >
              <MopButton
                mopVariant="primary"
                onClick={
                  inStoreTrainingButtonClicked
                    ? onClickAfterTrainingButtonClicked
                    : onClickBeforeTrainingButtonClicked
                }
              >
                {inStoreTrainingButtonClicked
                  ? t("uiMarkAsDoneAction")
                  : t("uiStoreTrainingButtonRedo")}
              </MopButton>
              {inStoreTrainingButtonClicked && (
                <Link
                  className={classes.trainingLink}
                  href={t("uiInstoreTrainingLink")}
                  onClick={() => {
                    logAmplitudeEvent(
                      "STATUS_PAGE",
                      "instore_training_card_retake_training_link_clicked"
                    );
                  }}
                  underline="always"
                  target="_blank"
                >
                  {t("uiStatusInStoreRetakeTraingLinkText")}
                </Link>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </StatusStep>
  );
};
